import React, { useCallback, useEffect, useState } from 'react';
import { GripVertical, Pencil, Plus, Trash2 } from 'lucide-react';
import { useSystem } from '@ohif/core';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@ohif/ui-next';

import ToggleSwitch from '../common/ToggleSwitch';
import AddStudyAssignmentRuleModal from '../modals/AddStudyAssignmentRuleModal';
import { ICON_STROKE_WIDTH } from '../../constants';
import { formatModalityDisplayLabel } from '../../utils/modalityUtils';
import { formatStoredTestNamesDisplay } from '../../utils/testNameListUtils';
import { formatPriorityDisplay } from '../worklist/imagingWorklistTableUtils';
import { authService } from '../../auth/authService';
import {
  getDicomRoutingRules,
  type DicomRoutingRule,
} from '../../services/dicomRoutingRuleService';
import {
  deleteStudyAssignmentRule,
  listStudyAssignmentRules,
  reorderStudyAssignmentRules,
  updateStudyAssignmentRule,
  type StudyAssignmentRule,
} from '../../services/studyAssignmentRuleService';
import { isSpokeFacilityUser } from '../../utils/permissions';
import { useReorderableRows } from '../../hooks';
import { moveItem } from '../../utils/moveItem';
import {
  buildModalityLabelOptions,
  findStudyAssignmentModalityConflict,
  hasPrefetchedCrossRuleValidationData,
} from '../../utils/risRuleModalityConflictUtils';

interface StudyAssignmentSectionProps {
  facilityId: number;
}

function formatAssigneeDisplay(rule: StudyAssignmentRule): string {
  if (rule.assignmentType === 'group') {
    const names = (rule.radiologists ?? []).map(rad => rad.fullName?.trim()).filter(Boolean);
    return names.length ? `Group: ${names.join(', ')}` : 'Group';
  }
  if (rule.assignmentType === 'least_loaded') {
    return 'Least-loaded (auto-pick)';
  }
  return rule.radiologist?.fullName?.trim() || `Radiologist #${rule.radiologistId}`;
}

function buildMatchCriteriaTags(rule: StudyAssignmentRule): string[] {
  const tags: string[] = [];
  if (rule.modalities?.length) {
    tags.push(...rule.modalities.map(modality => formatModalityDisplayLabel(modality.name)));
  }
  const subspecialtyLabel = formatStoredTestNamesDisplay(rule.matchSubspecialty);
  if (subspecialtyLabel) {
    tags.push(subspecialtyLabel);
  }
  if (rule.requiredExpertise?.trim()) {
    tags.push(`Specialty: ${rule.requiredExpertise.trim()}`);
  } else {
    tags.push('All specialties');
  }
  if (rule.matchPriority?.trim()) {
    tags.push(formatPriorityDisplay(rule.matchPriority) || rule.matchPriority);
  }
  return tags;
}

export default function StudyAssignmentSection({
  facilityId,
}: Readonly<StudyAssignmentSectionProps>) {
  const { servicesManager } = useSystem();
  const { uiNotificationService } = servicesManager.services;

  const [rules, setRules] = useState<StudyAssignmentRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [togglingId, setTogglingId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [reordering, setReordering] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<StudyAssignmentRule | null>(null);
  const [rulePendingDelete, setRulePendingDelete] = useState<StudyAssignmentRule | null>(null);
  const [activeRoutingRules, setActiveRoutingRules] = useState<DicomRoutingRule[] | null>(null);
  const isSpokeFacility = isSpokeFacilityUser(authService.getUser());

  const loadRules = useCallback(async () => {
    setLoading(true);
    setError(null);

    const assignmentPromise = listStudyAssignmentRules(facilityId);
    const routingPromise = isSpokeFacility
      ? getDicomRoutingRules()
      : Promise.resolve({ success: true as const, data: [] as DicomRoutingRule[] });

    const [assignmentRes, routingRes] = await Promise.all([assignmentPromise, routingPromise]);

    if (assignmentRes.success && Array.isArray(assignmentRes.data)) {
      setRules(assignmentRes.data);
    } else {
      setRules([]);
      setError(assignmentRes.error || 'Failed to load assignment rules');
    }

    if (isSpokeFacility) {
      if (routingRes.success && Array.isArray(routingRes.data)) {
        setActiveRoutingRules(routingRes.data.filter(rule => rule.is_active));
      } else {
        setActiveRoutingRules(null);
      }
    } else {
      setActiveRoutingRules([]);
    }

    setLoading(false);
  }, [facilityId, isSpokeFacility]);

  useEffect(() => {
    loadRules();
  }, [loadRules]);

  const handleToggleActive = async (rule: StudyAssignmentRule, checked: boolean) => {
    if (checked && hasPrefetchedCrossRuleValidationData(activeRoutingRules)) {
      const modalityConflict = findStudyAssignmentModalityConflict(
        rule.modalityIds ?? [],
        activeRoutingRules,
        buildModalityLabelOptions(rule, activeRoutingRules),
        true,
        'activate'
      );
      if (modalityConflict) {
        setError(modalityConflict);
        return;
      }
    }

    setTogglingId(rule.id);
    setError(null);
    const res = await updateStudyAssignmentRule(rule.id, {
      facilityId,
      isActive: checked,
    });
    setTogglingId(null);

    if (res.success && res.data) {
      setRules(prev => prev.map(item => (item.id === rule.id ? res.data! : item)));
      return;
    }

    setError(res.error || 'Failed to update rule status');
  };

  const handleDelete = async (rule: StudyAssignmentRule) => {
    setDeletingId(rule.id);
    setError(null);
    const res = await deleteStudyAssignmentRule(rule.id, facilityId);
    setDeletingId(null);
    setRulePendingDelete(null);

    if (res.success) {
      setRules(prev => prev.filter(item => item.id !== rule.id));
      uiNotificationService?.show({
        title: 'Rule deleted',
        message: `${rule.name} was removed.`,
        type: 'success',
        duration: 2500,
      });
      return;
    }

    setError(res.error || 'Failed to delete rule');
  };

  const persistReorder = useCallback(
    async (nextRules: StudyAssignmentRule[], previousRules: StudyAssignmentRule[]) => {
      setReordering(true);
      setRules(nextRules);
      const res = await reorderStudyAssignmentRules(
        nextRules.map(rule => rule.id),
        facilityId
      );
      setReordering(false);

      if (res.success && Array.isArray(res.data)) {
        setRules(res.data);
        return;
      }

      setRules(previousRules);
      setError(res.error || 'Failed to reorder rules');
    },
    [facilityId]
  );

  const handleReorder = useCallback(
    (fromIndex: number, toIndex: number) => {
      const previous = rules;
      const next = moveItem(rules, fromIndex, toIndex);
      void persistReorder(next, previous);
    },
    [rules, persistReorder]
  );

  const { dragIndex, getRowProps, getHandleProps } = useReorderableRows({
    disabled: reordering,
    onReorder: handleReorder,
  });

  const openAddModal = () => {
    setEditingRule(null);
    setModalOpen(true);
  };

  const openEditModal = (rule: StudyAssignmentRule) => {
    setEditingRule(rule);
    setModalOpen(true);
  };

  return (
    <section className="esc-step-card">
      <header className="esc-step-header">
        <div className="esc-step-title-row">
          <h2 className="esc-step-title">Study assignment rules</h2>
          <span className="esc-count-badge">{rules.length}</span>
        </div>
        <p className="esc-step-subtitle">
          Incoming studies are automatically assigned to a radiologist based on these rules.
          Most-specific match wins; subspecialty / expertise are matched against the doctor&apos;s
          profile. Drag rows to set priority when rules tie on specificity.
        </p>
      </header>

      <div className="esc-toolbar esc-toolbar--contacts">
        <div className="esc-toolbar-actions">
          <button
            type="button"
            className="esc-btn esc-btn--primary esc-btn--inline"
            onClick={openAddModal}
          >
            <Plus
              size={16}
              strokeWidth={ICON_STROKE_WIDTH}
            />
            Add rule
          </button>
        </div>
      </div>

      {error ? (
        <p
          className="esc-error-banner"
          role="alert"
        >
          {error}
        </p>
      ) : null}

      <div className="esc-table-wrap">
        <table className="esc-table">
          <thead>
            <tr>
              <th
                scope="col"
                className="esc-table-drag-col"
              >
                <span className="sr-only">Order</span>
              </th>
              <th scope="col">#</th>
              <th scope="col">Name</th>
              <th scope="col">Match criteria</th>
              <th scope="col">Radiologist</th>
              <th scope="col">Active</th>
              <th
                className="esc-table-actions-col"
                aria-label="Actions"
              />
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td
                  colSpan={7}
                  className="esc-table-empty"
                >
                  Loading assignment rules…
                </td>
              </tr>
            ) : rules.length === 0 ? (
              <tr>
                <td
                  colSpan={7}
                  className="esc-table-empty"
                >
                  No assignment rules configured. Studies will remain unassigned until manually
                  claimed.
                </td>
              </tr>
            ) : (
              rules.map((rule, index) => {
                const tags = buildMatchCriteriaTags(rule);
                const assigneeLabel = formatAssigneeDisplay(rule);
                return (
                  <tr
                    key={rule.id}
                    {...getRowProps(index)}
                    className={dragIndex === index ? 'esc-table-row--dragging' : undefined}
                  >
                    <td className="esc-table-drag-col">
                      <span
                        className="esc-drag-handle"
                        aria-hidden="true"
                        {...getHandleProps(index)}
                      >
                        <GripVertical
                          size={16}
                          strokeWidth={ICON_STROKE_WIDTH}
                        />
                      </span>
                    </td>
                    <td className="esc-table-index">{index + 1}</td>
                    <td className="esc-table-name">{rule.name}</td>
                    <td>
                      <div className="esc-tag-list">
                        {tags.length ? (
                          tags.map(tag => (
                            <span
                              key={`${rule.id}-${tag}`}
                              className="esc-tag"
                            >
                              {tag}
                            </span>
                          ))
                        ) : (
                          <span className="esc-tag esc-tag--muted">Any study</span>
                        )}
                      </div>
                    </td>
                    <td className="esc-table-muted">{assigneeLabel}</td>
                    <td className="esc-table-active-col">
                      <ToggleSwitch
                        checked={rule.isActive}
                        disabled={togglingId === rule.id || reordering}
                        onChange={checked => handleToggleActive(rule, checked)}
                      />
                    </td>
                    <td className="esc-table-actions-col">
                      <div className="esc-table-actions">
                        <button
                          type="button"
                          className="esc-icon-btn"
                          aria-label={`Edit ${rule.name}`}
                          onClick={() => openEditModal(rule)}
                        >
                          <Pencil
                            size={16}
                            strokeWidth={ICON_STROKE_WIDTH}
                          />
                        </button>
                        <button
                          type="button"
                          className="esc-icon-btn esc-icon-btn--danger"
                          aria-label={`Delete ${rule.name}`}
                          disabled={deletingId === rule.id}
                          onClick={() => setRulePendingDelete(rule)}
                        >
                          <Trash2
                            size={16}
                            strokeWidth={ICON_STROKE_WIDTH}
                          />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <AddStudyAssignmentRuleModal
        isOpen={modalOpen}
        facilityId={facilityId}
        editingRule={editingRule}
        activeRoutingRules={activeRoutingRules}
        onClose={() => setModalOpen(false)}
        onSubmit={loadRules}
      />

      <Dialog
        open={Boolean(rulePendingDelete)}
        onOpenChange={open => !open && !deletingId && setRulePendingDelete(null)}
      >
        <DialogContent className="esc-contact-dialog max-w-[480px] bg-white text-[#0a0a0a]">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold">Delete assignment rule</DialogTitle>
          </DialogHeader>

          <div className="esc-activate-dialog-body">
            <p>
              Are you sure you want to delete{' '}
              <strong>{rulePendingDelete?.name ?? 'this rule'}</strong>? This action cannot be
              undone.
            </p>
          </div>

          <div className="esc-dialog-actions">
            <button
              type="button"
              className="esc-btn esc-btn--secondary"
              disabled={deletingId != null}
              onClick={() => setRulePendingDelete(null)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="esc-btn esc-btn--primary study-assignment-delete-confirm-btn"
              disabled={deletingId != null}
              onClick={() => rulePendingDelete && void handleDelete(rulePendingDelete)}
            >
              {deletingId != null ? 'Deleting…' : 'Delete'}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}
